import React, { useMemo, useState } from 'react';
import {
  Box,
  Collapse,
  createStyles,
  Grid,
  IconButton,
  List,
  ListItem,
  makeStyles,
  Paper,
} from '@material-ui/core';

import { Section } from '@lib/common';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { format } from 'date-fns';

const useStyles = makeStyles(theme =>
  createStyles({
    section: {
      position: 'sticky',
      top: '3.5rem',
      backgroundColor: theme.palette.background.default,
      zIndex: 5,
      overflowY: 'scroll',
    },
  })
);

interface Props {
  isMobile?: boolean;
  filters?: Record<string, any>;
  children: NonNullable<React.ReactNode>;
}

export const FilterSection: React.FC<Props> = ({ children, isMobile, filters }) => {
  const { section } = useStyles();
  const [open, setOpen] = useState(true);

  const activeFilters = useMemo(() => {
    const active = Object.entries(filters ?? {}).filter(([key, value]) => {
      if (key.includes('Id') || key.includes('id')) return false;
      if (value === undefined || value === null) return false;
      if (typeof value === 'string' && value.trim() === '') return false;
      if (Array.isArray(value) && value.length === 0) return false;
      return !!value;
    });

    return active.map(([key, value]) => [
      key,
      value instanceof Date ? format(value, 'M/d/yyyy') : value,
    ]);
  }, [filters]);

  return (
    <Section className={section} disableGutters>
      <Paper elevation={2}>
        <Box padding={1}>
          {!open && activeFilters.length > 0 && (
            <Box alignItems="center" justifyContent="center" padding={1}>
              <Grid xs={12} container alignItems="center" justifyContent="center">
                {activeFilters.length} Active Filter{activeFilters.length > 1 ? 's' : ''}
              </Grid>
              <Grid xs={12} container alignItems="center" justifyContent="center">
                <List>
                  {activeFilters.slice(0, 5).map(([key, value]) => (
                    <ListItem key={key} style={{ padding: 0 }}>
                      {HumanizeKey(key)} {typeof value === 'boolean' ? '' : `: ${value.toString()}`}
                    </ListItem>
                  ))}
                  {activeFilters.length > 5 && (
                    <ListItem style={{ padding: 0 }}>
                      and {activeFilters.length - 5} more...
                    </ListItem>
                  )}
                </List>
              </Grid>
            </Box>
          )}
          <Collapse in={open} timeout="auto">
            {children}
          </Collapse>
        </Box>
        <IconButton
          onClick={() => setOpen(x => !x)}
          data-testid="toggleFilterButton"
          style={{
            width: '100%',
            backgroundColor: 'transparent',
            display: isMobile ? 'block' : 'none',
          }}
          disableRipple
        >
          <FontAwesomeIcon icon={open ? 'angle-up' : 'angle-down'} />
        </IconButton>
      </Paper>
    </Section>
  );
};

function HumanizeKey(key: string) {
  const value = key
    .split(/((?<=[a-z])(?=[A-Z])(?![A-Z][a-z])|(?=[A-Z](?=[a-z])))/)
    ?.filter(s => s !== '')
    .join(' ');

  const [first, ...rest] = value.startsWith('is ') ? value.substring(3, value.length) : value;

  return first.toUpperCase() + rest.join('');
}
